import { Chapter } from "./Chapter";
import { Reveal } from "./Reveal";
import { LineCat } from "./CatCrest";
import { PawWatermark } from "./Paws";
import { photos } from "../data/siteConfig";
import {
    BedDouble,
    ArrowUpFromLine,
    Sun,
    ShieldCheck,
    Crown,
    HeartHandshake,
} from "lucide-react";

const FEATURES = [
    {
        icon: BedDouble,
        title: "A suite of their own",
        text: "Every cat has individual accommodation with a cosy bed, blankets, fresh water, food and water bowls, litter facilities and cubby holes for hiding and resting.",
        testid: "feature-suite",
    },
    {
        icon: ArrowUpFromLine,
        title: "Climb, perch, observe",
        text: "High rafters, elevated areas and top-of-enclosure resting spots — room to climb, explore indoors and survey their kingdom from above.",
        testid: "feature-climb",
    },
    {
        icon: Sun,
        title: "The great outdoors",
        text: "A large, fully enclosed and secured outdoor area. Cats can move between indoor and outdoor areas during the day — sunshine and fresh air, safely contained.",
        testid: "feature-outdoor",
    },
    {
        icon: ShieldCheck,
        title: "Secure by design",
        text: "A multi-door secure entry system — sliding entrance door, gated office area, then a second gated entrance into the cattery — keeps escape risk to a minimum.",
        testid: "feature-security",
    },
    {
        icon: Crown,
        title: "The Penthouse",
        text: "A larger private area for cats who prefer their own space — no need to mix directly with other cats. A little luxury for the more solitary soul.",
        testid: "feature-penthouse",
    },
    {
        icon: HeartHandshake,
        title: "Every temperament welcome",
        text: "Confident or cautious, social or solitary — we read each individual cat and give them the right balance of attention, stimulation and personal space.",
        testid: "feature-temperament",
    },
];

// Curated editorial layout for authentic photos: one large feature
// (portrait-friendly) plus two supporting images — never a flat grid.
const PhotoTrio = ({ items }) => {
    const [feature, ...rest] = items;
    return (
        <div className="mt-16 grid gap-5 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
                <div className="spotlight-frame group h-full overflow-hidden rounded-3xl border border-stone-200 shadow-lg shadow-stone-900/10">
                    <img
                        src={feature.src}
                        alt={feature.alt}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                        style={{ aspectRatio: feature.orientation === "landscape" ? "4/3" : "4/5" }}
                        data-testid="cattery-photo-feature"
                    />
                </div>
            </Reveal>
            <div className="grid gap-5 lg:col-span-7">
                {rest.slice(0, 2).map((p, i) => (
                    <Reveal key={p.src} delay={0.1 + 0.08 * i}>
                        <div className="spotlight-frame group overflow-hidden rounded-3xl border border-stone-200 shadow-md shadow-stone-900/5">
                            <img
                                src={p.src}
                                alt={p.alt}
                                loading="lazy"
                                className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                                style={{ aspectRatio: "16/9" }}
                                data-testid={`cattery-photo-${i}`}
                            />
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    );
};

export const Cattery = () => (
    <section
        className="relative overflow-hidden bg-[#F2F1E9] py-24 text-[#1c1917] sm:py-32"
        data-testid="cattery-section"
    >
        <PawWatermark tone="light" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
            <Chapter
                no="02"
                id="cattery"
                eyebrow="The Cattery"
                title={
                    <>
                        Room to explore, relax, climb —{" "}
                        <em>and just be a cat.</em>
                    </>
                }
                intro="Every cat has their own individual space, with room to climb, explore, rest and watch the world go by — indoors and out."
            />

            <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {FEATURES.map((f, i) => (
                    <Reveal key={f.title} delay={0.06 * i}>
                        <article
                            className="group h-full rounded-2xl border border-stone-200 bg-white p-7 shadow-sm transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-fern/50 hover:shadow-md"
                            data-testid={f.testid}
                        >
                            <f.icon
                                className="h-7 w-7 text-fern transition-transform duration-300 group-hover:scale-110"
                                aria-hidden="true"
                            />
                            <h3 className="mt-5 font-serif text-2xl font-semibold">
                                {f.title}
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed text-stone-600 sm:text-base">
                                {f.text}
                            </p>
                        </article>
                    </Reveal>
                ))}
            </div>

            {photos.cattery.length > 0 ? (
                <PhotoTrio items={photos.cattery} />
            ) : photos.catteryFeature ? (
                <Reveal delay={0.1}>
                    <div
                        className="spotlight-frame mt-16 overflow-hidden rounded-3xl border border-stone-300 shadow-md"
                        data-testid="cattery-photo-main"
                    >
                        <img
                            src={photos.catteryFeature.src}
                            alt={photos.catteryFeature.alt}
                            loading="lazy"
                            className="aspect-[16/10] w-full object-cover object-[70%_55%] sm:aspect-[21/9]"
                        />
                    </div>
                </Reveal>
            ) : (
                <Reveal delay={0.1}>
                    <div
                        className="mt-16 flex flex-col items-center justify-center gap-4 rounded-3xl border border-dashed border-stone-300 bg-white px-8 py-16 text-center"
                        data-testid="cattery-photos-placeholder"
                    >
                        <LineCat className="h-12 w-12 text-stone-300" />
                        <p className="font-serif text-2xl italic text-stone-500">
                            More photos from the cattery coming soon.
                        </p>
                    </div>
                </Reveal>
            )}

            <Reveal delay={0.15}>
                <p
                    className="mt-10 max-w-2xl font-mono text-xs uppercase leading-relaxed tracking-[0.2em] text-stone-500"
                    data-testid="cattery-note"
                >
                    We never force cats to mix — safety and individual
                    temperament always come first.
                </p>
            </Reveal>
        </div>
    </section>
);
